<template>
  <div class="statusCard shadowed">
    <div class="headerRow">
      <!-- Name / Date -->
      <div class="nameAndCreated">
        <div class="name">{{ formattedType }}</div>
        <div class="description pt-1">{{ formattedDescription }}</div>
      </div>

      <!-- Status -->
      <div class="status">
        <div class="statusLabel">Current Status:</div>
        <div style="width: fit-content" class="statusName my-1 INFO">
          {{ formattedStatus }}
        </div>
      </div>
    </div>

    <div v-if="keyDocument.attachments.length" class="documentSection">
      <div class="statusLabel pb-2">Your documents are available to download:</div>
      <DocumentUploadWidget :documents="keyDocument?.attachments" @view="downloadDocument($event)" readonly />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { notifyNetworkError } from '@/core/utils';
import { Citizen, Core } from "@minicity/core";
import { computed, } from 'vue';
import DocumentUploadWidget from '@/components/DocumentUploadWidget.vue';
import { getAttachmentViewLink } from './api';

const props = defineProps<{
  keyDocumentType: string,
  keyDocument: Citizen.KeyDocument,
}>();

const formattedType = computed(() => {
  switch (props.keyDocumentType) {
    case "BIRTH_RECORD": return "Birth Record";
    case "SOCIAL_SECURITY": return "Social Security";
    case "STATE_ID": return "State ID";
    case "VETERAN_ID": return "Veteran ID";
    case "GEORGIA_DFCS_BENEFITS": return "Georgia DFCS Benefits";
    default: return "Unknown";
  }
});

const formattedStatus = computed(() => {
  switch (props.keyDocument.status) {
    case 'REQUIRED': return "Preparing Application";
    case 'APPLIED': return "Applied";
    case 'OBTAINED': return "Approved";
    default: return "Unknown";
  }
});

const formattedDescription = computed(() => {
  switch (props.keyDocument.status) {
    case 'REQUIRED': return "Your caseworker is preparing to apply for your documents.";
    case 'APPLIED': return "Your application has been submitted, and we are waiting for the response.";
    case 'OBTAINED': return "Your application was successful - contact your caseworker to collect your documents.";
    default: return "Unknown";
  }
});

const downloadDocument = (doc: Core.Document) => {
  getAttachmentViewLink(doc)
    .then((attachmentViewLink: string) => window.open(attachmentViewLink))
    .catch(err => notifyNetworkError("Failed to download attachment", err));
};

</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.statusCard {
  border: 1px solid rgb(180,210,180);
  border-radius: 1rem;
  width: min(100%, 800px);
  padding: 0rem;
}

.headerRow {
  display: flex;
  flex-direction: row;
  padding: 2.5rem 2.5rem 1.5rem 2.5rem;
  text-align: left;
}

.nameAndCreated {
  width: 65%;
}

.status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  
  width: 35%;
}

@media screen and (max-width: 800px) {
  .headerRow {
    flex-direction: column;
    text-align: center;
    padding: 1rem;
  }

  .nameAndCreated {
    width: 100%;
    margin-bottom: 1rem;
  }

  .status {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: unset;
  }

  .statusName {
    align-self: center;
  }
}

.documentSection {
  padding: 1rem 2.5rem 1.5rem 2.5rem;
  text-align: left;
}

.name {
  font-size: 125%;
  font-weight: bold;
}

.created {
  font-weight: bold;
}
.statusLabel {
  font-weight: bold;
  font-size: 1.10rem;
}

.statusName {
  padding: 0.25rem 1.25rem 0.25rem 1.25rem;
  background-color: #006600;
  border-radius: 1rem;

  min-width: 8rem;
  text-align: center;

  font-weight: bold;
  font-size: 0.9rem;
  color: white;
}

.statusName.ACTION_REQUIRED {
  background-color: #b98d12;
}
</style>
